import * as React from 'react';

import {
  Button,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogOverlay,
  DropZone,
  humanReadableFileSize,
} from '@rematter/ui-web';
import { useToast } from '@rematter/ui-web/hooks';
import { useAtom, useSetAtom } from 'jotai';
import { UploadIcon, X } from 'lucide-react';
import { isFileDropItem } from 'react-aria-components';

import { $uploadFileToCurrentDirectory } from '../../_state/fileActions.state';
import { $showUploadDialog } from '../../_state/fileDialogs.state';
import { useTranslations } from '../../i18n/useTranslations';

interface FileToUpload {
  file: File;
  progress: number;
  status: 'pending' | 'uploading' | 'complete' | 'error';
}

export const FileUploadDialog = () => {
  const t = useTranslations();
  const toast = useToast();
  const [isOpen, setIsOpen] = useAtom($showUploadDialog);
  const uploadFile = useSetAtom($uploadFileToCurrentDirectory);
  const [filesToUpload, setFilesToUpload] = React.useState<FileToUpload[]>([]);
  const [isUploading, setIsUploading] = React.useState(false);

  const handleDrop = async (
    e: Parameters<
      NonNullable<React.ComponentProps<typeof DropZone>['onDrop']>
    >[0],
  ) => {
    // Filter for file items only
    const fileItems = e.items.filter((item) => isFileDropItem(item));
    if (!fileItems.length) return;

    // Get files from drop items
    const filePromises = fileItems.map((item) => item.getFile());
    const files = await Promise.all(filePromises);

    const newFiles = files.map((file) => ({
      file,
      progress: 0,
      status: 'pending' as const,
    }));

    setFilesToUpload((prev) => [...prev, ...newFiles]);
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files) return;

    const newFiles = Array.from(files).map((file) => ({
      file,
      progress: 0,
      status: 'pending' as const,
    }));

    setFilesToUpload((prev) => [...prev, ...newFiles]);
  };

  const removeFile = (index: number) => {
    setFilesToUpload((prev) => prev.filter((_, i) => i !== index));
  };

  const handleUpload = async () => {
    setIsUploading(true);

    for (let i = 0; i < filesToUpload.length; i++) {
      const fileToUpload = filesToUpload[i];

      try {
        // Update status to uploading
        setFilesToUpload((prev) => {
          const updated = [...prev];
          updated[i] = { ...updated[i], status: 'uploading', progress: 50 };
          return updated;
        });

        await uploadFile(fileToUpload.file);

        // Update status to complete
        setFilesToUpload((prev) => {
          const updated = [...prev];
          updated[i] = { ...updated[i], status: 'complete', progress: 100 };
          return updated;
        });
      } catch {
        // Update status to error
        setFilesToUpload((prev) => {
          const updated = [...prev];
          updated[i] = { ...updated[i], status: 'error', progress: 0 };
          return updated;
        });

        toast.error(
          t.dialogs.upload.errorFailed.replace(
            '{filename}',
            fileToUpload.file.name,
          ),
        );
      }
    }

    setIsUploading(false);

    // Close dialog after a short delay to show completion
    setTimeout(() => {
      setIsOpen(false);
      setFilesToUpload([]);
    }, 1000);
  };

  return (
    <DialogOverlay
      isOpen={isOpen}
      onOpenChange={setIsOpen}
      className="@FileUploadDialog"
    >
      <DialogContent position="center">
        <DialogHeader>
          <DialogTitle className="text-xl font-semibold">
            {t.dialogs.upload.title}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4 p-4">
          {/* Drop Zone */}
          <DropZone
            onDrop={handleDrop}
            className="h-[200px] w-full border-2 border-dashed border-neutral/20"
          >
            <label className="flex size-full cursor-pointer flex-col items-center justify-center p-4">
              <input
                type="file"
                className="sr-only"
                onChange={handleFileSelect}
                multiple
                disabled={isUploading}
              />
              <UploadIcon className="mb-2 size-8 text-neutral/40" />
              <p className="text-sm text-neutral/60">
                {t.dialogs.upload.dropzone}
              </p>
            </label>
          </DropZone>

          {/* File List */}
          {filesToUpload.length > 0 && (
            <div className="space-y-2">
              <h3 className="text-sm font-medium">
                {t.dialogs.upload.filesToUpload}
              </h3>
              <div className="max-h-64 space-y-2 overflow-y-auto">
                {filesToUpload.map((fileToUpload, index) => (
                  <div
                    key={index}
                    className="flex items-center justify-between rounded-lg bg-neutral/5 p-3"
                  >
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-sm font-medium">
                        {fileToUpload.file.name}
                      </p>
                      <p className="text-xs text-neutral/50">
                        {humanReadableFileSize(fileToUpload.file.size)}
                      </p>
                    </div>

                    {fileToUpload.status === 'uploading' && (
                      <div className="ml-4 h-2 w-24 rounded-full bg-neutral/20">
                        <div
                          className="h-2 rounded-full bg-primary/50 transition-all"
                          style={{ width: `${fileToUpload.progress}%` }}
                        />
                      </div>
                    )}

                    {fileToUpload.status === 'complete' && (
                      <span className="ml-4 text-sm text-success/50">✓</span>
                    )}

                    {fileToUpload.status === 'error' && (
                      <span className="ml-4 text-sm text-failure/50">
                        {t.dialogs.upload.statusFailed}
                      </span>
                    )}

                    {fileToUpload.status === 'pending' && !isUploading && (
                      <button
                        onClick={() => removeFile(index)}
                        className="ml-4 text-neutral/40 hover:text-neutral/60"
                      >
                        <X className="size-4" />
                      </button>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onPress={() => {
              setIsOpen(false);
              setFilesToUpload([]);
            }}
            isDisabled={isUploading}
          >
            {t.dialogs.upload.cancel}
          </Button>
          <Button
            variant="solid"
            onPress={handleUpload}
            isDisabled={filesToUpload.length === 0 || isUploading}
          >
            {isUploading ? t.dialogs.upload.uploading : t.dialogs.upload.upload}
          </Button>
        </DialogFooter>
      </DialogContent>
    </DialogOverlay>
  );
};
